import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import {
  ArrowLeft, CircleDollarSign, Cloud, FileText, Folder, Home, LayoutDashboard,
  LineChart, Settings,
} from 'lucide-react';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import TabTechnical from './components/TabTechnical';
import TabMarket from './components/TabMarket';
import TabMetering from './components/TabMetering';
import TabForecasting from './components/TabForecasting';
import TabFinancial from './components/TabFinancial';
import InverterTelemetryWidget from './components/InverterTelemetryWidget';

// Область у полі region приходить з бекенду англійською (геокодер);
// перекладаємо відомі назви українських областей на українську, а решту
// (інші країни, нетипові підписи) лишаємо як є — краще показати оригінал,
// ніж мовчки зіпсувати незнайому назву.
const REGION_UA: Record<string, string> = {
  'cherkasy oblast': 'Черкаська область',
  'chernihiv oblast': 'Чернігівська область',
  'chernivtsi oblast': 'Чернівецька область',
  'dnipropetrovsk oblast': 'Дніпропетровська область',
  'dnipro oblast': 'Дніпропетровська область',
  'donetsk oblast': 'Донецька область',
  'ivano-frankivsk oblast': 'Івано-Франківська область',
  'kharkiv oblast': 'Харківська область',
  'kherson oblast': 'Херсонська область',
  'khmelnytskyi oblast': 'Хмельницька область',
  'kirovohrad oblast': 'Кіровоградська область',
  'kyiv oblast': 'Київська область',
  'luhansk oblast': 'Луганська область',
  'lviv oblast': 'Львівська область',
  'mykolaiv oblast': 'Миколаївська область',
  'odesa oblast': 'Одеська область',
  'odessa oblast': 'Одеська область',
  'poltava oblast': 'Полтавська область',
  'rivne oblast': 'Рівненська область',
  'sumy oblast': 'Сумська область',
  'ternopil oblast': 'Тернопільська область',
  'vinnytsia oblast': 'Вінницька область',
  'volyn oblast': 'Волинська область',
  'zakarpattia oblast': 'Закарпатська область',
  'zaporizhzhia oblast': 'Запорізька область',
  'zhytomyr oblast': 'Житомирська область',
  'autonomous republic of crimea': 'Автономна Республіка Крим',
  'kyiv city': 'м. Київ',
  'sevastopol': 'Севастополь',
};
const regionUa = (v: string | null | undefined): string => {
  if (!v) return '—';
  return REGION_UA[v.trim().toLowerCase()] ?? v;
};

interface AssetDetail {
  id: string;
  assetName: string;
  assetType: string;
  eicCode: string;
  eicWCode?: string | null;
  installedCapacityMw: number;
  region: string;
  locationAddress: string;
  latitude?: number;
  longitude?: number;
  commissioningDate?: string;
  operationalStatus: string;
  counterpartyId: string;
  counterpartyName: string;
  technicalSpec?: {
    inverterType?: string;
    inverterCount?: number;
    panelType?: string;
    panelCount?: number;
    batteryCapacityMwh?: number | null;
    gridConnectionVoltage?: string;
    meteringPointCode?: string;
    scadaAvailable?: boolean;
    scadaVendor?: string;
    communicationProtocol?: string;
    dataRefreshInterval?: string;
    tiltAngleDeg?: number | null;
    azimuthDeg?: number | null;
    installedCapacityDcMw?: number | null;
    installedCapacityAcMw?: number | null;
    panelNominalPowerW?: number | null;
    inverterTotalPowerKw?: number | null;
    dcAcRatio?: number | null;
  };
  marketConfig?: {
    marketModel?: string;
    balancingGroup?: string;
    imbalanceResponsibleParty?: string;
    tradingZone?: string;
    settlementModel?: string;
  };
  meteringIntegrations?: Array<{
    id: string;
    integrationType: string;
    endpointUrl: string;
    protocol: string;
    authType: string;
    status: string;
  }>;
  forecastingConfigs?: Array<{
    id: string;
    forecastProvider: string;
    forecastType: string;
    updateFrequency: string;
    forecastHorizon: string;
    responsibleParty: string;
  }>;
  financialTerms?: Array<{
    id: string;
    currency: string;
    pricingModel: string;
    priceFormula?: string;
    paymentTerms: string;
    settlementPeriod: string;
  }>;
  contracts?: Array<{
    id: string;
    contractType: string;
    contractNumber: string;
    startDate: string;
    endDate: string;
    status: string;
  }>;
  documents?: Array<{
    id: string;
    documentType: string;
    fileName: string;
    status: string;
    uploadedAt: string;
  }>;
}

// Іконки — Lucide, як усюди в новому коді кабінету (Remix у ньому заборонений).
const TABS = [
  { key: 'overview', labelKey: 'asset_tab_overview', Icon: Home },
  { key: 'technical', labelKey: 'asset_tab_technical', Icon: Settings },
  { key: 'market', labelKey: 'asset_tab_market', Icon: LineChart },
  { key: 'metering', labelKey: 'asset_tab_metering', Icon: LayoutDashboard },
  { key: 'forecasting', labelKey: 'asset_tab_forecasting', Icon: Cloud },
  { key: 'financial', labelKey: 'asset_tab_financial', Icon: CircleDollarSign },
  { key: 'contracts', labelKey: 'asset_tab_contracts', Icon: FileText },
  { key: 'documents', labelKey: 'asset_tab_documents', Icon: Folder },
];

const WIZARD_TABS = ['technical', 'market', 'metering', 'forecasting', 'financial'];

/**
 * Один рядок картки «Розташування»: label + value як ДВІ клітинки
 * батьківської grid-розкладки (не власний div-wrapper — інакше зламав би
 * двоколонковий потік). Єдине місце, що визначає цю пару, тож усі три
 * поля (регіон/адреса/координати) лишаються структурно ідентичними.
 */
function LocRow({ label, value, textMuted, textMain, wrap, mono }: {
  label: string; value: string; textMuted: string; textMain: string; wrap?: boolean; mono?: boolean;
}) {
  return (
    <>
      <span style={{ color: textMuted }}>{label}</span>
      <span className={`font-medium${wrap ? ' break-words' : ''}${mono ? ' tabular-nums text-xs' : ''}`} style={{ color: textMain }}>{value}</span>
    </>
  );
}

function EicInlineEditRow({ label, field, value, assetId, onSaved }: { label: string; field: 'eicCode' | 'eicWCode'; value: string; assetId: string; onSaved: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';

  useEffect(() => { setDraft(value); }, [value]);

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string | null> = {};
      payload[field] = draft.trim() === '' ? null : draft.trim();
      await api.patch(`/assets/${assetId}`, payload);
      addToast(t('common_saved'), 'success');
      setEditing(false);
      onSaved();
    } catch (e) {
      addToast(getApiErrorMessage(e, t('common_save_failed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex justify-between items-center gap-2 text-sm">
        <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{label}</span>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            className="text-xs px-2 py-1 rounded tabular-nums outline-none"
            style={{ border: '1px solid var(--accent)', color: 'var(--text)', minWidth: 160 }}
            placeholder={field === 'eicCode' ? '62X...' : '62W...'}
          />
          <button onClick={save} disabled={saving} title={t('common_save')} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
            <i className="ri-check-line text-xs"></i>
          </button>
          <button onClick={() => { setDraft(value); setEditing(false); }} title={t('common_cancel')} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      </div>
    );
  }
  // Іконка редагування — ПЕРЕД значенням (не після тексту), щоб вона не
  // «плавала» на різній відстані від лівого краю через різну довжину EIC-кодів.
  return (
    <div className="flex justify-between items-center text-sm group">
      <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{label}</span>
      <span className="flex items-center gap-1">
        <button onClick={() => setEditing(true)} title={`Edit ${label}`} className="w-5 h-5 rounded flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200" style={{ color: 'var(--series-3)' }}>
          <i className="ri-edit-line text-xs"></i>
        </button>
        <span className="tabular-nums font-medium text-sm" style={{ color: textMain }}>{value || '—'}</span>
      </span>
    </div>
  );
}

export default function AssetDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const [data, setData] = useState<AssetDetail | null>(null);
  const { setSelectedAsset } = useSelectedAsset();
  const [loading, setLoading] = useState(true);

  // Сторінка більше не тримає власної палітри з парами isDark ? … : … —
  // усі поверхні, лінії й текст беруться з токенів кабінету (design/tokens.css).
  // Обидві теми задаються в одному місці, як на решті редизайнених сторінок.
  const cardBg = 'var(--surface-solid)';
  const cardBorder = 'var(--line)';
  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const sectionBg = 'var(--surface-2)';
  const tableHeaderBg = 'var(--surface-2)';
  const tableBorder = 'var(--line)';
  const hoverBg = 'var(--surface-2)';

  const tabParam = searchParams.get('tab');
  const isWizard = searchParams.get('wizard') === 'true';
  const activeTab = tabParam && TABS.find((t) => t.key === tabParam) ? tabParam : 'overview';

  const setActiveTab = (key: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', key);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (id) loadAsset();
  }, [id]);

  const loadAsset = async () => {
    setLoading(true);
    try {
      const result = await api.get<AssetDetail>(`/assets/${id}`);
      setData(result);
        // sync Topbar selector with the asset opened by URL
        setSelectedAsset({
          id: result.id,
          assetName: result.assetName,
          installedCapacityMw: Number(result.installedCapacityMw),
          region: result.region,
          operationalStatus: result.operationalStatus,
          counterpartyName: result.counterpartyName,
        });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setData(null);
      } else {
        const message = getApiErrorMessage(err, t('asset_load_failed'));
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = () => {
    loadAsset();
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: 'var(--ok)' },
      planned: { bg: 'rgba(107,107,112,0.1)', color: 'var(--text-muted)' },
      construction: { bg: 'rgba(184,134,11,0.1)', color: 'var(--warn)' },
      commissioning: { bg: 'rgba(31,91,143,0.1)', color: 'var(--series-3)' },
      suspended: { bg: 'color-mix(in srgb, var(--warn) 12%, transparent)', color: 'var(--warn)' },
      decommissioned: { bg: 'rgba(107,107,112,0.1)', color: 'var(--text-muted)' },
    };
    const style = map[status] ?? map.planned;
    return (
      <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  // Wizard progress bar
  const WizardProgress = () => {
    if (!isWizard) return null;
    const currentIdx = WIZARD_TABS.indexOf(activeTab);
    return (
      <div className="rounded-xl p-4 mb-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
            {t('asset_wizard_title')}
          </span>
          <button
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete('wizard');
              setSearchParams(params, { replace: true });
            }}
            className="text-xs cursor-pointer hover:underline"
            style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}
          >
            {t('asset_wizard_exit')}
          </button>
        </div>
        <div className="flex items-center gap-0">
          {WIZARD_TABS.map((tabKey, idx) => {
            const tab = TABS.find((t) => t.key === tabKey);
            const isDone = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            return (
              <div key={tabKey} className="flex items-center flex-1">
                <button
                  onClick={() => setActiveTab(tabKey)}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors"
                    style={{
                      background: isDone ? 'var(--ok)' : isCurrent ? 'var(--accent)' : 'var(--surface-2)',
                      color: isDone || isCurrent ? 'var(--accent-contrast)' : 'var(--text-muted)',
                    }}
                  >
                    {isDone ? <i className="ri-check-line text-xs"></i> : idx + 2}
                  </div>
                  <span
                    className="text-xs font-medium hidden sm:block whitespace-nowrap"
                    style={{ color: isCurrent ? 'var(--accent)' : isDone ? 'var(--ok)' : 'var(--text-muted)' }}
                  >
                    {tab ? t(tab.labelKey) : ''}
                  </span>
                </button>
                {idx < WIZARD_TABS.length - 1 && (
                  <div className="flex-1 h-px mx-2" style={{ background: isDone ? 'var(--ok)' : 'var(--line)' }}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumb={['Assets', 'Loading...']}>
        <div className="p-12 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
          <p className="text-sm mt-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>Loading asset...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout breadcrumb={['Assets', 'Not Found']}>
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: 'var(--accent)' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>{t('asset_not_found')}</p>
          <button
            onClick={() => navigate('/assets')}
            className="mt-4 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
          >
            {t('asset_back_to_list')}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const currentWizardIdx = WIZARD_TABS.indexOf(activeTab);
  const canGoNext = isWizard && currentWizardIdx >= 0 && currentWizardIdx < WIZARD_TABS.length - 1;
  const canGoPrev = isWizard && currentWizardIdx > 0;

  // Хвіст крихти — узагальнена назва розділу. Назву активу показує селектор
  // у топбарі, тож у заголовку сторінки вона лише дублювалась.
  return (
    <DashboardLayout breadcrumb={['Assets', t('asset_section')]}>
      {/* Ідентифікаційний рядок активу. Заголовок з назвою СЕС і бейдж стану
          прибрані: назву показує селектор у топбарі, стан — вкладка «Overview»
          та колонка «Статус» у переліку. Ритм рівний: відступ згори дає падинг
          шару (p-4 / md:p-6), такий самий mb-4 / md:mb-6 йде до ряду кнопок. */}
      <div className="flex items-center gap-3 flex-wrap mb-4 md:mb-6">
          <button
            onClick={() => navigate('/assets')}
            title={t('asset_back')}
            aria-label={t('asset_back')}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer shrink-0 transition-colors"
            style={{ border: '1px solid var(--line-strong)' }}
          >
            <ArrowLeft size={16} strokeWidth={2} style={{ color: textMuted }} />
          </button>
          <div className="flex items-center gap-4 text-[12.5px] flex-wrap min-w-0" style={{ color: textMuted }}>
          {/* tabular-nums контракт забороняє; рівність цифр дає tabular-nums. */}
          <span>EIC X: <strong className="font-semibold" style={{ color: textMain, fontVariantNumeric: 'tabular-nums' }}>{data.eicCode}</strong></span>{data.eicWCode && (<><span>·</span><span>EIC W: <strong className="font-semibold" style={{ color: textMain, fontVariantNumeric: 'tabular-nums' }}>{data.eicWCode}</strong></span></>)}
          <span>·</span>
          <span>{data.installedCapacityMw} МВт</span>
          <span>·</span>
          <span className="capitalize">{data.assetType.replace(/_/g, ' ')}</span>
          <span>·</span>
          <span>{regionUa(data.region)}</span>
          {data.counterpartyName && (
            <>
              <span>·</span>
              <button
                onClick={() => navigate(`/counterparties/${data.counterpartyId}`)}
                className="cursor-pointer hover:underline"
                style={{ color: 'var(--accent)' }}
              >
                {data.counterpartyName}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Wizard progress */}
      <WizardProgress />

      {/* Вкладки — спільний SegmentedTabs кабінету (той самий, що в SCADA й
          «Подіях»), у режимі iconOnly: квадрати 40×40, підпис у tooltip.
          Брендового червоного в активному стані немає — §3 лишає його за
          реальною помилкою. */}
      <div className="flex items-center mb-4 md:mb-6">
        <SegmentedTabs
          variant="outline"
          iconOnly
          items={TABS.map((tab) => ({ key: tab.key, label: t(tab.labelKey), Icon: tab.Icon }))}
          activeKey={activeTab}
          onSelect={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      {/* Полотно розділу. Це НЕ картка: інакше була б картка в картці —
          велика панель, а в ній ще плитки. Блоки нижче лежать просто на
          тлі сторінки й відділяються від нього самі — тонким контуром і
          мʼякою тінню .tile. */}
      <div className="flex flex-col gap-5">
        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="tile p-4">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_sec_general')}</div>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_name')}</span>
                    <span className="font-medium" style={{ color: textMain }}>{data.assetName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_type')}</span>
                    <span className="font-medium capitalize" style={{ color: textMain }}>{data.assetType.replace(/_/g, ' ')}</span>
                  </div>
                  <EicInlineEditRow
                    label="EIC Code (X)"
                    field="eicCode"
                    value={data.eicCode}
                    assetId={data.id}
                    onSaved={loadAsset}
                  />
                  <EicInlineEditRow
                    label="EIC Code (W)"
                    field="eicWCode"
                    value={data.eicWCode || ''}
                    assetId={data.id}
                    onSaved={loadAsset}
                  />
                  <div className="flex justify-between text-sm">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_capacity')}</span>
                    <span className="font-medium" style={{ color: textMain }}>{data.installedCapacityMw} МВт</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_status')}</span>
                    {getStatusBadge(data.operationalStatus)}
                  </div>
                </div>
              </div>

              <div className="tile p-4">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_sec_location')}</div>
                {/* Grid label/value замість вертикального списку: ліва колонка —
                    стабільна ширина під найдовший підпис («Координати»), права
                    бере решту й переносить текст (адреса тепер не розпирає
                    картку). На мобільному звужується назад в один стовпець —
                    підпис над значенням, щоб текст не стискався. LocRow —
                    єдине джерело цієї розмітки, рядки нижче лише передають
                    дані, стилі не дублюються. */}
                <div className="grid grid-cols-1 sm:grid-cols-[92px_minmax(0,1fr)] gap-x-3 gap-y-2.5 text-sm">
                  <LocRow label={t('asset_f_region')} value={regionUa(data.region)} textMuted={textMuted} textMain={textMain} />
                  <LocRow label={t('asset_f_address')} value={data.locationAddress} textMuted={textMuted} textMain={textMain} wrap />
                  {data.latitude != null && (
                    <LocRow label={t('asset_f_coordinates')} value={`${data.latitude}, ${data.longitude}`} textMuted={textMuted} textMain={textMain} mono />
                  )}
                </div>
                {data.commissioningDate && (
                  <div className="flex justify-between text-sm mt-3 pt-2.5" style={{ borderTop: `1px solid ${cardBorder}` }}>
                    <span style={{ color: textMuted }}>{t('asset_f_commissioned')}</span>
                    <span className="font-medium text-right" style={{ color: textMain }}>
                      {new Date(data.commissioningDate).toLocaleDateString('uk-UA')}
                    </span>
                  </div>
                )}
              </div>

              <div className="tile p-4">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_sec_progress')}</div>
                <div className="space-y-2">
                  {[
                    { label: t('asset_tab_technical'), key: 'technical', done: !!data.technicalSpec },
                    { label: t('asset_tab_market'), key: 'market', done: !!data.marketConfig },
                    { label: t('asset_tab_metering'), key: 'metering', done: (data.meteringIntegrations?.length ?? 0) > 0 },
                    { label: t('asset_tab_forecasting'), key: 'forecasting', done: (data.forecastingConfigs?.length ?? 0) > 0 },
                    { label: t('asset_tab_financial'), key: 'financial', done: (data.financialTerms?.length ?? 0) > 0 },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => setActiveTab(item.key)}
                      className="w-full flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors hover:bg-[color-mix(in_srgb,var(--text)_6%,transparent)]"
                    >
                      <span className="text-sm font-medium" style={{ color: textMain }}>{item.label}</span>
                      {item.done ? (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--ok)' }}>
                          <i className="ri-check-line"></i> Done
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--warn)' }}>
                          <i className="ri-time-line"></i> Pending
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <InverterTelemetryWidget assetId={data.id} />
          </div>
        )}

        {/* Technical Spec */}
        {activeTab === 'technical' && (
          <div className="tile p-6">
          <TabTechnical
            assetId={data.id}
            initialData={data.technicalSpec}
            onSaved={handleSaved}
          />
          </div>
        )}

        {/* Market Config */}
        {activeTab === 'market' && (
          <div className="tile p-6">
          <TabMarket
            assetId={data.id}
            initialData={data.marketConfig}
            onSaved={handleSaved}
          />
          </div>
        )}

        {/* Metering / SCADA */}
        {activeTab === 'metering' && (
          <div className="tile p-6">
          <TabMetering
            assetId={data.id}
            integrations={data.meteringIntegrations}
            onSaved={handleSaved}
          />
          </div>
        )}

        {/* Forecasting */}
        {activeTab === 'forecasting' && (
          <div className="tile p-6">
          <TabForecasting
            assetId={data.id}
            configs={data.forecastingConfigs}
            onSaved={handleSaved}
          />
          </div>
        )}

        {/* Financial Terms */}
        {activeTab === 'financial' && (
          <div className="tile p-6">
          <TabFinancial
            assetId={data.id}
            terms={data.financialTerms}
            onSaved={handleSaved}
          />
          </div>
        )}

        {/* Contracts */}
        {activeTab === 'contracts' && (
          <div className="tile p-6">
            <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>{t('common_contracts')}</h3>
            {data.contracts && data.contracts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: sectionBg }}>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_number')}</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_type')}</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_period')}</th>
                      <th className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_f_status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contracts.map((c) => (
                      <tr key={c.id} style={{ borderBottom: '1px solid var(--line)' }}>
                        <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{c.contractNumber}</td>
                        <td className="px-4 py-3 capitalize" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{c.contractType.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-xs" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
                          {new Date(c.startDate).toLocaleDateString('uk-UA')} — {new Date(c.endDate).toLocaleDateString('uk-UA')}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(c.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                  <i className="ri-file-list-3-line text-xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
                </div>
                <p className="text-sm" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_no_contracts')}</p>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div className="tile p-6">
            <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>{t('common_documents')}</h3>
            {data.documents && data.documents.length > 0 ? (
              <div className="space-y-2">
                {data.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg" style={{ border: '1px solid var(--line)' }}>
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-lg" style={{ color: 'var(--accent)' }}></i>
                      <div>
                        <div className="text-sm font-medium" style={{ color: textMain }}>{doc.fileName}</div>
                        <div className="text-xs" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>
                          {doc.documentType} · {new Date(doc.uploadedAt).toLocaleDateString('uk-UA')}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(doc.status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                  <i className="ri-folder-line text-xl" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}></i>
                </div>
                <p className="text-sm" style={{ borderColor: textMuted, background: 'transparent', color: textMuted }}>{t('asset_no_documents')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wizard navigation buttons */}
      {isWizard && (canGoPrev || canGoNext) && (
        <div className="flex items-center justify-between mt-5">
          <button
            onClick={() => setActiveTab(WIZARD_TABS[currentWizardIdx - 1])}
            disabled={!canGoPrev}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-30 whitespace-nowrap"
            style={{ border: '1px solid var(--line)', color: 'var(--text)' }}
          >
            <i className="ri-arrow-left-line"></i> {t('asset_wizard_prev')}
          </button>
          {canGoNext ? (
            <button
              onClick={() => setActiveTab(WIZARD_TABS[currentWizardIdx + 1])}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              {t('asset_wizard_next')} <i className="ri-arrow-right-line"></i>
            </button>
          ) : (
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('wizard');
                params.set('tab', 'overview');
                setSearchParams(params, { replace: true });
                addToast('Asset specification complete!', 'success');
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
              style={{ background: 'var(--ok)' }}
            >
              <i className="ri-check-line"></i> {t('asset_wizard_done')}
            </button>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
